/** Helpers for the time rate screen. */
import { todayIso } from "./rateModel";

export type TimeRate = {
  id: string;
  personId?: string | null;
  activityId?: string | null;
  activityName?: string | null;
  source: "PERSON_ACTIVITY" | "PERSON_GENERAL" | "ACTIVITY_DEFAULT";
  basis: "HOURLY" | "DAILY";
  amount?: string | null;
  hoursPerDay?: string | null;
  hourlyRate?: string | null;
  minuteRate?: string | null;
  overtime?: { afterHoursPerDay: string; mode: "RATE" | "MULTIPLIER"; value: string } | null;
  effectiveFrom: string;
  effectiveTo?: string | null;
  status: "DRAFT" | "APPROVED" | "DISCARDED";
  note?: string | null;
  approvedByName?: string | null;
};

export type RateState = "draft" | "current" | "upcoming" | "ended";

export const rateState = (r: TimeRate, today: string): RateState => {
  if (r.status === "DRAFT") return "draft";
  if (r.effectiveFrom > today) return "upcoming";
  if (r.effectiveTo && r.effectiveTo < today) return "ended";
  return "current";
};

/** Group a list of rates by activity ("" = any activity), newest first. */
export const byActivity = (rates: TimeRate[]): Map<string, TimeRate[]> => {
  const m = new Map<string, TimeRate[]>();
  for (const r of rates) {
    const k = r.activityId || "";
    m.set(k, [...(m.get(k) || []), r]);
  }
  m.forEach((list) => list.sort((a, b) => (a.effectiveFrom < b.effectiveFrom ? 1 : -1)));
  return m;
};

/** A kept rate as stored, up to 4 decimals: 47.0588 → "₹47.0588", 50 → "₹50", 0.8333 → "₹0.8333". */
export const rateRupees = (v?: string | number | null): string => {
  const n = Number(v);
  if (v === null || v === undefined || v === "" || !isFinite(n)) return "—";
  return `₹${n.toFixed(4).replace(/\.?0+$/, "")}`;
};

/** ₹/day over the hours of a working day, kept per hour and per minute (4 decimals, as the server keeps them). */
export const splitDayRate = (perDay: number, hoursPerDay: number): { hourly: number; perMinute: number } => ({
  hourly: Math.round((perDay / hoursPerDay) * 10000) / 10000,
  perMinute: Math.round((perDay / (hoursPerDay * 60)) * 10000) / 10000,
});

/** How time pay was worked out: 210 min → "3 h × ₹47.0588 + 30 min × ₹0.7843". */
export const payWorking = (minutes: number, hourlyRate?: string | null, minuteRate?: string | null): string => {
  if (!minutes || !hourlyRate || !minuteRate) return "";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const parts = [h ? `${h} h × ${rateRupees(hourlyRate)}` : "", m ? `${m} min × ${rateRupees(minuteRate)}` : ""];
  return parts.filter(Boolean).join(" + ");
};

export const describeRate = (r: TimeRate): string => {
  const kept = r.minuteRate ? `${rateRupees(r.hourlyRate)}/hour, ${rateRupees(r.minuteRate)}/minute` : `${rateRupees(r.hourlyRate)}/hour`;
  const base =
    r.basis === "DAILY"
      ? `₹${r.amount}/day (${r.hoursPerDay || "8"} h) = ${kept}`
      : r.minuteRate
      ? `₹${r.amount}/hour = ${rateRupees(r.minuteRate)}/minute`
      : `₹${r.amount}/hour`;
  if (!r.overtime) return base;
  const ot =
    r.overtime.mode === "RATE" ? `₹${r.overtime.value}/hour` : `${r.overtime.value}× the usual rate`;
  return `${base}; overtime after ${r.overtime.afterHoursPerDay} h at ${ot}`;
};

export type RateForm = {
  id?: string;
  basis: "HOURLY" | "DAILY";
  amount: string;
  hoursPerDay: string;
  effectiveFrom: string;
  note: string;
  overtimeOn: boolean;
  overtimeAfterHours: string;
  overtimeMode: "MULTIPLIER" | "RATE";
  overtimeValue: string;
};

export const emptyForm = (): RateForm => ({
  basis: "HOURLY",
  amount: "",
  hoursPerDay: "8",
  effectiveFrom: todayIso(),
  note: "",
  overtimeOn: false,
  overtimeAfterHours: "8",
  overtimeMode: "MULTIPLIER",
  overtimeValue: "1.5",
});

export const formFrom = (r: TimeRate): RateForm => ({
  id: r.id,
  basis: r.basis,
  amount: r.amount || "",
  hoursPerDay: r.hoursPerDay || "8",
  effectiveFrom: r.effectiveFrom,
  note: r.note || "",
  overtimeOn: !!r.overtime,
  overtimeAfterHours: r.overtime?.afterHoursPerDay || "8",
  overtimeMode: r.overtime?.mode || "MULTIPLIER",
  overtimeValue: r.overtime?.value || "1.5",
});

export const formBody = (f: RateForm, withOvertime: boolean) => ({
  basis: f.basis,
  amount: f.amount.trim(),
  hoursPerDay: f.basis === "DAILY" ? f.hoursPerDay.trim() : "",
  effectiveFrom: f.effectiveFrom,
  note: f.note,
  overtimeAfterHours: withOvertime && f.overtimeOn ? f.overtimeAfterHours.trim() : "",
  overtimeMode: f.overtimeMode,
  overtimeValue: withOvertime && f.overtimeOn ? f.overtimeValue.trim() : "",
});

/** "2:30" or "2.5" hours → minutes; null if unreadable. */
export const toMinutes = (raw: string): number | null => {
  const v = (raw || "").trim();
  if (!v) return null;
  const hm = v.match(/^(\d{1,2}):(\d{1,2})$/);
  if (hm) return Number(hm[1]) * 60 + Number(hm[2]);
  if (/^\d+(\.\d+)?$/.test(v)) return Math.round(Number(v) * 60);
  return null;
};

export const hoursLabel = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
};
